import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, CircularProgress, Typography, Alert } from "@mui/material";
import { Document, Page, pdfjs } from "react-pdf";
import { apiClient } from "../api";
import { logAxiosError } from "../utils/logAxiosError";

// Misma versión que usa react-pdf (evita mezclar worker de pdfjs-dist 4.x del proyecto con core 5.x).
const PDFJS_VER = pdfjs.version || "5.4.296";
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${PDFJS_VER}/build/pdf.worker.min.mjs`;

const SCROLL_END_THRESHOLD_PX = 48;

/**
 * Visor PDF con react-pdf. Habilita `onReachedEnd` cuando el usuario hace scroll hasta el final
 * del contenedor (o el contenido cabe sin scroll).
 */
const StudentCoursePdfViewer = ({ idCurso, archivoRuta, onReachedEnd, width = 720 }) => {
  const scrollRef = useRef(null);
  const reachedEndRef = useRef(false);
  const [blobUrl, setBlobUrl] = useState("");
  const [loadingPdf, setLoadingPdf] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [numPages, setNumPages] = useState(0);
  const [reachedEnd, setReachedEnd] = useState(false);

  useEffect(() => {
    reachedEndRef.current = false;
    setReachedEnd(false);
    setNumPages(0);
    setLoadError("");
    setLoadingPdf(true);
    let revoked = false;
    let objectUrl = "";

    const load = async () => {
      if (!idCurso || !archivoRuta) {
        setLoadingPdf(false);
        setLoadError("No hay archivo para esta sección.");
        return;
      }
      try {
        const { data } = await apiClient.get(`cursos/${idCurso}/secciones/descargar`, {
          params: { archivoUrl: archivoRuta },
          responseType: "blob",
          headers: {
            Accept: "application/pdf, application/octet-stream, */*",
          },
        });
        if (revoked) return;
        if (!data || data.size === 0) {
          setLoadError("El archivo está vacío o no está disponible.");
          return;
        }
        const headerProbe = await data.slice(0, 5).text();
        if (!headerProbe.startsWith("%PDF")) {
          let detail = "El servidor no devolvió un PDF (¿sesión o permisos?).";
          try {
            const asText = await data.text();
            const parsed = JSON.parse(asText);
            detail = parsed?.message ?? parsed?.Message ?? parsed?.error ?? detail;
          } catch {
            /* cuerpo no JSON */
          }
          setLoadError(detail);
          return;
        }
        objectUrl = URL.createObjectURL(data);
        setBlobUrl(objectUrl);
      } catch (e) {
        logAxiosError(`Estudiante: descargar PDF (curso ${idCurso})`, e);
        if (!revoked) {
          setLoadError("No se pudo cargar el PDF. Intenta de nuevo más tarde.");
        }
      } finally {
        if (!revoked) setLoadingPdf(false);
      }
    };

    load();
    return () => {
      revoked = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [idCurso, archivoRuta]);

  const checkScrollEnd = useCallback(() => {
    const el = scrollRef.current;
    if (!el || reachedEndRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    if (scrollHeight <= clientHeight + SCROLL_END_THRESHOLD_PX) {
      reachedEndRef.current = true;
      setReachedEnd(true);
      onReachedEnd?.();
      return;
    }
    if (scrollTop + clientHeight >= scrollHeight - SCROLL_END_THRESHOLD_PX) {
      reachedEndRef.current = true;
      setReachedEnd(true);
      onReachedEnd?.();
    }
  }, [onReachedEnd]);

  useEffect(() => {
    if (numPages > 0) {
      requestAnimationFrame(() => checkScrollEnd());
    }
  }, [numPages, checkScrollEnd]);

  const onDocumentLoadSuccess = ({ numPages: n }) => {
    setNumPages(n);
  };

  if (loadError) {
    return (
      <Box py={4} textAlign="center">
        <Typography color="error">{loadError}</Typography>
      </Box>
    );
  }

  if (loadingPdf || !blobUrl) {
    return (
      <Box py={8} display="flex" justifyContent="center" alignItems="center" flexDirection="column" gap={2}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          Cargando documento…
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Alert severity={reachedEnd ? "success" : "info"} sx={{ mb: 1.5 }}>
        {reachedEnd
          ? "Llegaste al final del documento. El examen ya está habilitado."
          : "Desplázate hasta la última página del documento para habilitar el examen."}
      </Alert>
      <Box
        ref={scrollRef}
        onScroll={checkScrollEnd}
        sx={{
          maxHeight: { xs: "55vh", md: "65vh" },
          overflowY: "auto",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          bgcolor: "#525659",
          px: 1,
          py: 2,
        }}
      >
      <Document
        file={blobUrl}
        onLoadSuccess={onDocumentLoadSuccess}
        onLoadError={() => setLoadError("El archivo no es un PDF válido o está dañado.")}
        loading={
          <Box py={4} display="flex" justifyContent="center">
            <CircularProgress size={32} sx={{ color: "common.white" }} />
          </Box>
        }
      >
        {Array.from({ length: numPages }, (_, i) => (
          <Box key={`p-${i + 1}`} display="flex" justifyContent="center" mb={2}>
            <Page pageNumber={i + 1} width={width} renderTextLayer={false} renderAnnotationLayer={false} />
          </Box>
        ))}
      </Document>
      </Box>
    </Box>
  );
};

export default StudentCoursePdfViewer;
